// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/services/customerLifecycleApi.js

function normalizeBase(apiBaseUrl) {
  const raw = String(apiBaseUrl || "").trim().replace(/\/+$/, "");
  if (!raw) {
    throw new Error("Customer lifecycle API base URL is not configured.");
  }
  return raw;
}

function tryJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function detailMessage(payload, status) {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message;
    }
    try {
      return JSON.stringify(detail);
    } catch {
      // Fall through to the stable HTTP message.
    }
  }
  if (typeof payload?.error === "string" && payload.error.trim()) {
    return payload.error;
  }
  return `Customer lifecycle request failed (${status}).`;
}

export class CustomerLifecycleApiError extends Error {
  constructor(message, { status, code, payload } = {}) {
    super(message);
    this.name = "CustomerLifecycleApiError";
    this.status = status || null;
    this.code = code || null;
    this.payload = payload || null;
  }
}

async function lifecycleRequest({
  apiBaseUrl,
  getAccessToken,
  method,
  path,
  body,
  signal,
}) {
  if (typeof getAccessToken !== "function") {
    throw new Error("Customer lifecycle access-token provider is unavailable.");
  }

  const token = await getAccessToken();
  if (!token) {
    throw new Error("Unable to acquire an OASYS API access token.");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${normalizeBase(apiBaseUrl)}${path}`, {
    method,
    signal,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const responseText = await response.text();
  const payload = tryJson(responseText);

  if (!response.ok) {
    throw new CustomerLifecycleApiError(
      detailMessage(payload, response.status),
      {
        status: response.status,
        code: payload?.detail?.code || payload?.code || null,
        payload,
      }
    );
  }

  return payload || {};
}

export function resolveCustomerEntry({ apiBaseUrl, getAccessToken, signal }) {
  return lifecycleRequest({
    apiBaseUrl,
    getAccessToken,
    signal,
    method: "GET",
    path: "/customer-lifecycle/entry",
  });
}

export function bootstrapCustomerOrganization({
  apiBaseUrl,
  getAccessToken,
  fullName,
  organizationName,
  region = "us",
  dataResidencyPolicy = "US_ONLY",
  signal,
}) {
  return lifecycleRequest({
    apiBaseUrl,
    getAccessToken,
    signal,
    method: "POST",
    path: "/customer-lifecycle/bootstrap",
    body: {
      full_name: String(fullName || "").trim(),
      organization_name: String(organizationName || "").trim(),
      region,
      data_residency_policy: dataResidencyPolicy,
    },
  });
}

export function completeCustomerProfile({
  apiBaseUrl,
  getAccessToken,
  fullName,
  signal,
}) {
  return lifecycleRequest({
    apiBaseUrl,
    getAccessToken,
    signal,
    method: "PATCH",
    path: "/customer-lifecycle/profile",
    body: {
      full_name: String(fullName || "").trim(),
    },
  });
}
